import React, { useState, useEffect } from "react";
import { Dropdown, Row, Nav, Tab } from "react-bootstrap";
import Switch from "react-switch";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { IMAGES } from "../../constant/theme";
import { gridDataBlog } from "./GridData";
import {
  appApi,
  useGetAllStaffQuery,
  useGetSelfieConfigQuery,
} from "../../../store/appApi";
import { selectUserRole, selectUser } from "../../../store/authSlice";
import {
  updateUserStatusApi,
  updateUserAttendanceApi,
} from "../../../services/staff";
import { setSelfieConfigApi } from "../../../services/attendance";

const theadData = [
  { heading: "Name", sortingVale: "name" },
  { heading: "Role", sortingVale: "role" },
  { heading: "Mobile", sortingVale: "mobile" },
  { heading: "City", sortingVale: "city" },
  { heading: "Status", sortingVale: "status" },
  { heading: "Attendance", sortingVale: "attendance" },
  { heading: "Action", sortingVale: "action" },
];

const AllStaff = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "super-admin";
  const currentUserId = useSelector(selectUser)?.localId;

  const [sort, setSortata] = useState(10);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [togglingStatusId, setTogglingStatusId] = useState(null);
  const [togglingAttendanceId, setTogglingAttendanceId] = useState(null);

  // ── Global selfie-required toggle for punch in/out ─────────────────────────
  const { data: selfieConfigData } = useGetSelfieConfigQuery();
  const selfieRequired = selfieConfigData?.selfieRequired ?? true;
  const [togglingSelfie, setTogglingSelfie] = useState(false);

  const handleToggleSelfieConfig = async () => {
    setTogglingSelfie(true);
    try {
      await setSelfieConfigApi(!selfieRequired);
      dispatch(appApi.util.invalidateTags(["SelfieConfig"]));
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update selfie requirement.",
      });
    } finally {
      setTogglingSelfie(false);
    }
  };

  // ── Debounce search ────────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ── GET via Redux ──────────────────────────────────────────────────────────
  const {
    data: staffResponse,
    isLoading,
    isFetching,
  } = useGetAllStaffQuery({
    page,
    limit: Number(sort),
    search: debouncedSearch,
  });

  const rawList = Array.isArray(staffResponse?.staff)
    ? staffResponse.staff
    : [];
  const total = Number(staffResponse?.total) || rawList.length;
  const totalPages = Math.ceil(total / Number(sort)) || 1;

  const staffList = rawList
    .filter(
      (item) => (item._id?.toString() || item.id?.toString()) !== currentUserId,
    )
    .map((item) => ({
      id: item._id?.toString() || item.id?.toString() || "",
      name: item.name || "-",
      role: item.role || "-",
      mobile: item.phone || "-",
      city: item.city || "-",
      email: item.email || "-",
      status: item.status === true,
      attendanceEnabled: item.attendanceEnabled !== false,
    }));

  const paggination = Array(totalPages)
    .fill()
    .map((_, i) => i + 1);
  const showingFrom = total > 0 ? (page - 1) * Number(sort) + 1 : 0;
  const showingTo = total > 0 ? Math.min(page * Number(sort), total) : 0;
  const showLoading = isLoading || isFetching;

  // ── Toggle status — direct API call, no Redux ──────────────────────────────
  const handleToggleStatus = async (staff) => {
    const newStatus = !staff.status;
    setTogglingStatusId(staff.id);
    try {
      await updateUserStatusApi(staff.id, newStatus);
      dispatch(appApi.util.invalidateTags(["Staff"]));
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update status.",
      });
    } finally {
      setTogglingStatusId(null);
    }
  };

  // ── Toggle attendance — direct API call, no Redux ──────────────────────────
  const handleToggleAttendance = async (staff) => {
    const newStatus = !staff.attendanceEnabled;
    setTogglingAttendanceId(staff.id);
    try {
      await updateUserAttendanceApi(staff.id, newStatus);
      dispatch(appApi.util.invalidateTags(["Staff"]));
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update attendance.",
      });
    } finally {
      setTogglingAttendanceId(null);
    }
  };

  return (
    <>
      <PageTitle activeMenu={"All Staff"} motherMenu={"Staff"} />
      <Row>
        <Tab.Container defaultActiveKey={"List"}>
          {/* <div className="col-lg-12">
            <Nav as="ul" className="nav nav-pills mb-3">
              <Nav.Item as="li">
                <Nav.Link eventKey="List" className="me-1">List View</Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link eventKey="Grid">Grid View</Nav.Link>
              </Nav.Item>
            </Nav>
          </div> */}
          <div className="col-lg-12">
            <Tab.Content className="row tab-content">
              <Tab.Pane eventKey="List" className="col-lg-12">
                <div className="card">
                  <div className="card-header">
                    <h4 className="card-title">All Staff</h4>
                    <div className="d-flex align-items-center gap-3">
                      {isSuperAdmin && (
                        <label className="d-flex align-items-center gap-2 mb-0">
                          <Switch
                            onChange={handleToggleSelfieConfig}
                            checked={selfieRequired}
                            disabled={togglingSelfie}
                            onColor="#4d6542"
                            offColor="#ff5e5e"
                            uncheckedIcon={false}
                            checkedIcon={false}
                            height={20}
                            width={40}
                          />
                          <span className="small">
                            Selfie {selfieRequired ? "Required" : "Disabled"}{" "}
                            for Punch
                          </span>
                        </label>
                      )}
                      {isSuperAdmin && (
                        <Link to={"/add-staff"} className="btn btn-primary">
                          + Add New
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <div
                        id="holidayList"
                        className="dataTables_wrapper no-footer"
                      >
                        <div className="justify-content-between d-sm-flex">
                          <div className="dataTables_length">
                            <label className="d-flex align-items-center">
                              Show
                              <Dropdown className="search-drop">
                                <Dropdown.Toggle
                                  as="div"
                                  className="search-drop-btn"
                                >
                                  {sort}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setSortata(10);
                                      setPage(1);
                                    }}
                                  >
                                    10
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setSortata(20);
                                      setPage(1);
                                    }}
                                  >
                                    20
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setSortata(30);
                                      setPage(1);
                                    }}
                                  >
                                    30
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                              entries
                            </label>
                          </div>
                          {/* <div className="dataTables_filter">
                            <label>
                              Search:{" "}
                              <input
                                type="search"
                                className=""
                                placeholder=""
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                              />
                            </label>
                          </div> */}
                        </div>
                        <table
                          id="example4"
                          className="display dataTable no-footer w-100"
                        >
                          <thead>
                            <tr>
                              {theadData.map((item, ind) => (
                                <th key={ind}>{item.heading}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {showLoading && (
                              <tr>
                                <td colSpan={7}>Loading staff...</td>
                              </tr>
                            )}
                            {!showLoading && staffList.length === 0 && (
                              <tr>
                                <td colSpan={7}>No staff found.</td>
                              </tr>
                            )}
                            {!showLoading &&
                              staffList.map((data) => (
                                <tr key={data.id}>
                                  <td style={{ textTransform: "capitalize" }}>
                                    {data.name}
                                  </td>
                                  <td style={{ textTransform: "capitalize" }}>
                                    {data.role}
                                  </td>
                                  <td>{data.mobile}</td>
                                  <td style={{ textTransform: "capitalize" }}>
                                    {data.city}
                                  </td>
                                  <td>
                                    {isSuperAdmin ? (
                                      <Switch
                                        onChange={() =>
                                          handleToggleStatus(data)
                                        }
                                        checked={data.status}
                                        disabled={togglingStatusId === data.id}
                                        onColor="#4d6542"
                                        offColor="#ff5e5e"
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        height={22}
                                        width={44}
                                      />
                                    ) : (
                                      <span
                                        className={`badge ${data.status ? "badge-success" : "badge-danger"} light`}
                                      >
                                        {data.status ? "Active" : "Inactive"}
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {isSuperAdmin ? (
                                      <Switch
                                        onChange={() =>
                                          handleToggleAttendance(data)
                                        }
                                        checked={data.attendanceEnabled}
                                        disabled={
                                          togglingAttendanceId === data.id
                                        }
                                        onColor="#4d6542"
                                        offColor="#ff5e5e"
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        height={22}
                                        width={44}
                                      />
                                    ) : (
                                      <span
                                        className={`badge ${data.attendanceEnabled ? "badge-success" : "badge-danger"} light`}
                                      >
                                        {data.attendanceEnabled ? "On" : "Off"}
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <Link
                                        to={`/staff-profile/${data.id}`}
                                        className="btn btn-xs btn-outline-info"
                                      >
                                        View
                                      </Link>
                                      {isSuperAdmin && (
                                        <Link
                                          to={`/staff/attendance/employee/${data.id}`}
                                          className="btn btn-xs btn-outline-secondary"
                                        >
                                          Attendance
                                        </Link>
                                      )}
                                      {isSuperAdmin && (
                                        <Link
                                          to={`/edit-staff/${data.id}`}
                                          className="btn btn-xs sharp btn-primary"
                                        >
                                          <i className="fa fa-pencil" />
                                        </Link>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                        <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                          <div className="dataTables_info">
                            Showing {showingFrom} to {showingTo} of {total}{" "}
                            entries
                          </div>
                          <div
                            className="dataTables_paginate paging_simple_numbers"
                            id="example5_paginate"
                          >
                            <Link
                              className={`paginate_button previous ${page <= 1 ? "disabled" : ""}`}
                              to="#"
                              onClick={() => page > 1 && setPage(page - 1)}
                            >
                              Previous
                            </Link>
                            <span>
                              {paggination.map((number, i) => (
                                <Link
                                  key={i}
                                  to="#"
                                  className={`paginate_button ${page === number ? "current" : ""}`}
                                  onClick={() => setPage(number)}
                                >
                                  {number}
                                </Link>
                              ))}
                            </span>
                            <Link
                              className={`paginate_button next ${page >= totalPages ? "disabled" : ""}`}
                              to="#"
                              onClick={() =>
                                page < totalPages && setPage(page + 1)
                              }
                            >
                              Next
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Tab.Pane>
              <Tab.Pane eventKey="Grid" className="col-lg-12">
                <div className="row">
                  {gridDataBlog.map((item, ind) => (
                    <div
                      className="col-lg-4 col-md-6 col-sm-6 col-12"
                      key={ind}
                    >
                      <div className="card card-profile">
                        <div className="card-header justify-content-end pb-0 border-0">
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-link i-false"
                              type="button"
                            >
                              <span className="dropdown-dots fs--1"></span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu
                              align="end"
                              className="dropdown-menu dropdown-menu-right border py-0"
                            >
                              <div className="py-2">
                                <Link to={"#"} className="dropdown-item">
                                  Edit
                                </Link>
                                <Link
                                  to={"#"}
                                  className="dropdown-item text-danger"
                                >
                                  Delete
                                </Link>
                              </div>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                        <div className="card-body pt-2">
                          <div className="text-center">
                            <div className="profile-photo">
                              <img
                                src={item.image}
                                width="100"
                                className="img-fluid rounded-circle"
                                alt=""
                              />
                            </div>
                            <h3 className="mt-4 mb-1">{item.name}</h3>
                            <p className="text-muted">{item.subject}</p>
                            <ul className="list-group mb-3 list-group-flush">
                              {item.content.map((data, ind) => (
                                <li
                                  className="list-group-item px-0 d-flex justify-content-between"
                                  key={ind}
                                >
                                  <span className="mb-0">{data.title} :</span>
                                  <strong>{data.subtitle}</strong>
                                </li>
                              ))}
                            </ul>
                            <Link
                              to={"/professor-profile"}
                              className="btn btn-outline-primary btn-rounded mt-3 px-4"
                            >
                              Read More
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Tab.Pane>
            </Tab.Content>
          </div>
        </Tab.Container>
      </Row>
    </>
  );
};

export default AllStaff;
